import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Phone, Menu, X } from "lucide-react";
import { COMPANY } from "@/data/company";
import { fileUrl } from "@/lib/api";

const LINKS = [
  { to: "/", label: "Accueil" },
  { to: "/services", label: "Services" },
  { to: "/realisations", label: "Réalisations" },
  { to: "/conseils", label: "Conseils" },
  { to: "/faq", label: "FAQ" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const loc = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [loc.pathname]);

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-white/80 backdrop-blur-xl border-b border-white/40 shadow-sm"
          : "bg-gradient-to-b from-black/90 via-black/55 to-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-[72px]">
        <Link to="/" data-testid="navbar-logo" className="flex items-center gap-2">
          <img src={fileUrl(COMPANY.logo)} alt="SMT Provence" className="h-11 w-auto" />
        </Link>

        <nav className="hidden lg:flex items-center gap-1">
          {LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              data-testid={`nav-${l.label.toLowerCase()}`}
              className={({ isActive }) =>
                `px-3.5 py-2 text-sm font-semibold rounded-full transition-colors ${
                  isActive
                    ? "text-primary"
                    : scrolled
                    ? "text-ink/80 hover:text-primary"
                    : "text-white/90 hover:text-primary"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <a
            href={`tel:${COMPANY.phoneHref}`}
            data-testid="navbar-phone"
            className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-bold transition-colors ${
              scrolled ? "bg-ink text-white hover:bg-primary" : "bg-primary text-white hover:brightness-95"
            }`}
          >
            <Phone size={16} /> {COMPANY.phone}
          </a>
        </div>

        <button
          onClick={() => setOpen(!open)}
          data-testid="navbar-menu-toggle"
          className={`lg:hidden p-2 rounded-lg ${scrolled ? "text-ink" : "text-white"}`}
          aria-label="Menu"
        >
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden bg-white border-t border-border px-4 py-4 space-y-1" data-testid="mobile-menu">
          {LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              className={({ isActive }) =>
                `block px-4 py-3 rounded-lg text-base font-semibold ${
                  isActive ? "bg-accent text-primary" : "text-ink"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
          <a
            href={`tel:${COMPANY.phoneHref}`}
            className="mt-2 flex items-center justify-center gap-2 bg-primary text-white px-5 py-3 rounded-full font-bold"
          >
            <Phone size={18} /> {COMPANY.phone}
          </a>
        </div>
      )}
    </header>
  );
}
